import React from 'react';
import _ from 'lodash';
import classNames from 'classnames';
import { useSelector } from 'react-redux';
import TitleSection from './TitleSection';
import { Category, Store } from './types';

const Menu: React.FC<{
    title: string;
    categories: Category[];
    hash: string;
    backgroundImage: string;
    className?: string;
}> = ({ className, hash, title, backgroundImage, categories }) => {
    const allergens = useSelector(({ app }: Store) => app.allergens);
    const products = useSelector(({ app }: Store) => app.products);

    const [selectedCategory, setSelectedCategory] = React.useState<number>(null);
    const [filteredProducts, setFilteredProducts] = React.useState(products);

    const handleCategoryClick = React.useCallback(
        (id: number) => () => {
            setSelectedCategory(id);
            setFilteredProducts(
                products.filter(({ productCategoryID }) => productCategoryID === id)
            );
        },
        [products]
    );

    React.useEffect(() => {
        const categoryId = categories[0]?.id;

        if (!_.isNil(categoryId)) {
            handleCategoryClick(categoryId)();
        }
    }, [categories, products, handleCategoryClick]);

    return (
        <div
            className={classNames('restaurant-menu', className)}
            id={hash}
            style={{
                backgroundImage: `url(${backgroundImage})`,
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'center bottom',
                backgroundSize: 'cover'
            }}>
            <div className='container'>
                <TitleSection title={title} />
                <div className='categories'>
                    {categories.map(({ id, name }) => (
                        <div
                            className={classNames('category', {
                                selected: selectedCategory === id
                            })}
                            key={id}
                            onClick={handleCategoryClick(id)}>
                            {name}
                            <div />
                        </div>
                    ))}
                </div>
                <div className='menu-products'>
                    {_.chunk(
                        filteredProducts,
                        Math.ceil(filteredProducts.length / 2)
                    ).map((value, i) => (
                        <div className='product-chunk' key={i}>
                            {value.map(
                                ({
                                    name,
                                    id,
                                    description,
                                    productAllergens,
                                    price,
                                    isVegan,
                                    isVegetarian,
                                    productVarieties
                                }) => {
                                    const allergenCodes = productAllergens
                                        .map(
                                            ({ allergenID }) =>
                                                allergens.find(a => a.id === allergenID)
                                                    ?.code
                                        )
                                        .filter(c => !!c);

                                    const productPrice = productVarieties.length
                                        ? productVarieties.map(v => v.price).join('/')
                                        : price;

                                    const hasSinglePrice =
                                        !!categories.find(
                                            c => c.id === selectedCategory
                                        )?.productCategoryMiscInfo?.isWine ||
                                        !productVarieties.length;

                                    return (
                                        <div key={id} className='product'>
                                            <div
                                                className={classNames('left', {
                                                    full: !hasSinglePrice
                                                })}>
                                                <span className='description'>
                                                    {name} {description}
                                                </span>
                                                {!!allergenCodes.length && (
                                                    <span className='allergens'>
                                                        | {allergenCodes.join('/')}
                                                    </span>
                                                )}
                                                <div className='type'>
                                                    <span>
                                                        {isVegetarian && 'vegetarisch'}
                                                    </span>
                                                    <span>{isVegan && 'vegan'}</span>
                                                </div>
                                                {!hasSinglePrice && (
                                                    <div>
                                                        {productVarieties.map(p => (
                                                            <div
                                                                key={p.id}
                                                                className='variety'>
                                                                <span>{p.name}</span>
                                                                <div className='right'>
                                                                    € {p.price}
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                            {hasSinglePrice && (
                                                <div className='right'>
                                                    € {productPrice}
                                                </div>
                                            )}
                                        </div>
                                    );
                                }
                            )}
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default Menu;
